import SelectInfrastructure from '@/basicsComponents/Selects/SelectInfrastructure';
import {
	Combobox,
	ComboboxInput,
	ComboboxContent,
	ComboboxList,
	ComboboxItem,
	ComboboxEmpty,
} from '@hydrodigital/combobox';
import type { ComboboxOption } from '@hydrodigital/combobox';
import { Button, Icon, Modal } from '@hydrodigital/design-system';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Node } from 'reactflow';
import InfrastructureService from '../../domain/Infrastructure/InfraestructureService';
import { infraTypesById } from '../../statics/constants/infraTypes';
import './Sidebar.scss';

const infrastructureService = new InfrastructureService();

interface SideBarProps {
	onSave: Function;
	isEditMode: boolean;
	nodeSelect: any;
	locationId: any;
	onChangeParamNode: Function;
	closeEditMode: Function;
	nodes: Node[];
}

export const Sidebar: React.FC<SideBarProps> = ({
	onSave,
	isEditMode,
	nodeSelect,
	locationId,
	onChangeParamNode,
	closeEditMode,
	nodes,
}: SideBarProps) => {
	const navigate = useNavigate();
	const onDragStart = (event: React.DragEvent<HTMLDivElement>, nodeType: string) => {
		event.dataTransfer.setData('application/reactflow', nodeType);
		event.dataTransfer.effectAllowed = 'move';
	};
	const [parameters, setParameters] = useState<any[]>([]);
	const [selectedInfraestructure, setSelectedInfraestructure] = useState<any>(null);
	const [selectedParameter, setSelectedParameter] = useState<any>(null);
	const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

	const incompleteNodes = useMemo(() => {
		return nodes.filter(
			(node) =>
				node.type === 'parameter' &&
				(!node.data?.value?.paremeterId || !node.data?.value?.infrastructureId),
		);
	}, [nodes]);

	const hasIncompleteNodes = incompleteNodes.length > 0;

	// Reset selection state when node changes or edit mode is entered
	useEffect(() => {
		if (isEditMode && nodeSelect) {
			setSelectedInfraestructure(null);
			setSelectedParameter(null);
			setParameters([]);
		}
	}, [nodeSelect?.id, isEditMode]);

	const handleSaveClick = () => {
		if (hasIncompleteNodes) {
			setIsWarningModalOpen(true);
			return;
		}
		onSave();
	};

	return (
		<aside className="flex column">
			{isEditMode ? (
				<>
					<span
						style={{
							color: '#023E65',
							fontSize: '16px',
							fontWeight: '500',
						}}
						className="mb-3"
					>
						Parameter Node settings
					</span>
					<span style={{ color: '#232B36' }} className="mb-1">
						Select Asset
					</span>
					<SelectInfrastructure
						mode="ByLocation"
						location={locationId}
						emitObject
						labelInValue
						defaultActiveFirstOption={false}
						onSelect={async (val) => {
							if (val && val.id) {
								setSelectedInfraestructure(
									JSON.stringify({
										id: val.id,
										name: val.name,
										type: infraTypesById[val.infraTypeId as keyof typeof infraTypesById],
									}),
								);
								// Use parameters from selected infrastructure if available
								if (val.parameters && val.parameters.length > 0) {
									setParameters(val.parameters);
								} else {
									// Fetch parameters if not included
									try {
										const infraDetails = await infrastructureService.getInfrastructureWithParams(
											String(val.id),
										);
										// Handle both parametersDTOList and measurementParameters
										const params = infraDetails?.parametersDTOList || 
											infraDetails?.measurementParameters || 
											infraDetails?.parameters || [];
										setParameters(params);
									} catch (error) {
										console.error('Error fetching infrastructure parameters:', error);
										setParameters([]);
									}
								}
							} else {
								setSelectedInfraestructure(null);
								setParameters([]);
							}
							setSelectedParameter(null);
						}}
					/>
					<div className="mt-2">
						<Combobox
							items={(parameters || []).map((p): ComboboxOption => {
								const selectedInfra = selectedInfraestructure
									? JSON.parse(selectedInfraestructure)
									: null;
								// Normalize parameter fields to match expected API format
								const paramId = p.paremeterId || p.id;
								const paramName = p.paremeterName || p.name;

								// Normalize sources array to expected format
								const normalizedSources = (p.sources || []).map((s: any) => ({
									sourceId: s.sourceId || s.id,
									serialNumber: s.serialNumber || s.sourceSerialNumber,
									sourceName: s.sourceName || s.name,
									historicalMetService: s.historicalMetService || null,
									forecastMetService: s.forecastMetService || null,
								}));

								return {
									value: JSON.stringify({
										paremeterId: paramId,
										paremeterName: paramName,
										paremeterTypeId: p.paremeterTypeId || p.parameterTypeId || null,
										paremeterTypeName: p.paremeterTypeName || p.parameterTypeName || null,
										serialNumberSource: p.serialNumberSource || null,
										unit: p.unit || p.parameterUnit || null,
										parameterPrincipal: p.parameterPrincipal ?? p.principal ?? true,
										inpId: p.inpId || 0,
										inpObjectId: p.inpObjectId || null,
										inpObjectType: p.inpObjectType || 0,
										sources: normalizedSources,
										infrastructureId: selectedInfra?.id,
										infrastructure: selectedInfra,
									}),
									label: paramName,
								};
							})}
							value={selectedParameter ?? undefined}
							onValueChange={(val) => {
								setSelectedParameter(val || null);
							}}
							onClear={() => {
								setSelectedParameter(null);
							}}
							size="small"
						>
							<ComboboxInput placeholder="Select parameter" label="Select parameter" showLabel />
							<ComboboxContent>
								<ComboboxEmpty>No parameters found.</ComboboxEmpty>
								<ComboboxList>
									{(item) => (
										<ComboboxItem key={item.value} value={item}>
											{item.label}
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>
					<div className="flex justify-content-around mt-auto gap-2">
						<Button
							label="Accept"
							showIcon={false}
							onClick={() => {
								if (selectedParameter) {
									onChangeParamNode(JSON.parse(selectedParameter), nodeSelect.id);
									closeEditMode();
									setSelectedInfraestructure(null);
									setSelectedParameter(null);
									setParameters([]);
								}
							}}
						/>

						<Button
							label="Cancel"
							variant="secondary"
							showIcon={false}
							onClick={() => {
								closeEditMode();
								setSelectedInfraestructure(null);
								setSelectedParameter(null);
							}}
						/>
					</div>
				</>
			) : (
				<>
					<span>Flow nodes</span>
					<div className="description">You can drag these nodes to the pane on the left.</div>
					<div
						className="dndnode parameter"
						onDragStart={(event) => onDragStart(event, 'parameter')}
						draggable
					>
						Parameter Node
					</div>
					<div className="flex justify-content-around mt-auto gap-2">
						<Button
							label="Save"
							showIcon={false}
							onClick={handleSaveClick}
							variant={hasIncompleteNodes ? 'secondary' : 'primary'}
						/>

						<Button
							label="Cancel"
							variant="secondary"
							showIcon={false}
							onClick={() => {
								navigate('/block/diagram');
							}}
						/>
					</div>
				</>
			)}

			<Modal
				isOpen={isWarningModalOpen}
				onClose={() => setIsWarningModalOpen(false)}
				size="s"
				closeOnOverlayClick={true}
				closeOnEscape={true}
				showCloseButton={false}
			>
				<Modal.Header>
					<div className="flex items-center gap-2">
						<Icon name="exclamation-triangle" size="m" color="#F59E0B" />
						<Modal.Title text="Incomplete Parameter Nodes" />
					</div>
				</Modal.Header>
				<Modal.Content>
					<div className="text-center">
						<p>
							There {incompleteNodes.length === 1 ? 'is' : 'are'}{' '}
							<strong>{incompleteNodes.length}</strong> parameter node
							{incompleteNodes.length === 1 ? '' : 's'} that{' '}
							{incompleteNodes.length === 1 ? 'has' : 'have'} not been configured.
						</p>
						<p className="mt-2">
							Please link each parameter node to an asset and parameter before saving, or
							delete the incomplete nodes.
						</p>
					</div>
				</Modal.Content>
				<Modal.Footer>
					<Button
						label="Got it"
						showIcon={false}
						onClick={() => setIsWarningModalOpen(false)}
					/>
				</Modal.Footer>
			</Modal>
		</aside>
	);
};
